/*
 * TW header (sections/header-tw.liquid): opens the menu cards, keeps the chosen area and scopes
 * the Workshops card to it. The only network call is the area box's store search, made once
 * someone has typed two letters (Shopify predictive search, JSON).
 */
(() => {
  const root = document.querySelector('[data-tw-hdr]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  const KEY = 'tw:area';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const all = (sel, scope = document) => Array.from(scope.querySelectorAll(sel));

  /* ---------------------------------------------------------------- area */

  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } };
  const save = (area) => { try { localStorage.setItem(KEY, JSON.stringify(area)); } catch (e) {} };

  const optionFor = (key) => key && root.querySelector(`[data-tw-area="${CSS.escape(key)}"]`);

  const apply = (area) => {
    const key = area && area.k;
    const option = optionFor(key);
    const name = option ? option.dataset.name : '';

    all('[data-tw-area-label]').forEach((el) => { el.textContent = name || root.dataset.areaDefault; });
    all('[data-tw-area]', root).forEach((el) => el.setAttribute('aria-current', String(el.dataset.twArea === key)));
    all('[data-tw-area-clear]', root).forEach((el) => el.setAttribute('aria-current', String(!name)));

    all('[data-tw-area-heading]').forEach((el) => {
      el.textContent = name && el.dataset.tpl ? el.dataset.tpl.replace('[area]', name) : el.dataset.plain;
    });
    all('[data-tw-area-all]').forEach((el) => {
      const scoped = name && el.dataset.tpl && option.dataset.url;
      el.textContent = scoped ? el.dataset.tpl.replace('[area]', name) : el.dataset.plain;
      el.href = scoped ? option.dataset.url : el.dataset.href;
    });
    all('[data-areas]').forEach((el) => { el.hidden = Boolean(name) && !el.dataset.areas.includes(`|${key}|`); });
    all('[data-tw-area-cities]').forEach((el) => {
      el.hidden = name ? el.dataset.twAreaCities !== key : el.dataset.twAreaCities !== '';
    });
  };

  const clearArea = () => {
    try { localStorage.removeItem(KEY); } catch (e) {}
    apply(null);
    document.dispatchEvent(new CustomEvent('tw:area', { detail: { k: '', n: '', source: 'clear' } }));
  };

  const choose = (key, source) => {
    const option = optionFor(key);
    if (!option) return;
    const area = { k: key, n: option.dataset.name };
    save(area);
    apply(area);
    document.dispatchEvent(new CustomEvent('tw:area', { detail: { ...area, source } }));
    try { window.clarity && window.clarity('set', 'tw_area', area.n); } catch (e) {}
    try { window.gtag && window.gtag('event', 'select_area', { area: area.n, source }); } catch (e) {}
    return option;
  };

  // A page that belongs to an area (an event page, an area collection) sets it for the visitor.
  if (root.dataset.pageArea) save({ k: root.dataset.pageArea, n: root.dataset.pageAreaName });
  apply(read());

  /* --------------------------------------------------------------- cards */

  let current = null;
  // A card opened by a choice (not by hover) stays until the pointer has been in it and left,
  // so it does not flash and vanish when the pointer happens to be outside the header.
  let pinned = false;
  let closeTimer = 0;
  let openTimer = 0;

  const place = (panel, anchor) => {
    panel.style.setProperty('--tw-x', '0px');
    const box = root.getBoundingClientRect();
    const a = anchor.getBoundingClientRect();
    const width = panel.firstElementChild ? panel.firstElementChild.offsetWidth : panel.offsetWidth;
    const gutter = 16;
    let x = a.left - box.left - 8;
    x = Math.min(x, box.width - width - gutter);
    x = Math.max(x, gutter);
    panel.style.setProperty('--tw-x', `${Math.round(x)}px`);
  };

  const close = () => {
    clearTimeout(openTimer);
    if (!current) return;
    current.panel.hidden = true;
    current.trigger.setAttribute('aria-expanded', 'false');
    current.host.removeAttribute('open');
    current = null;
    pinned = false;
  };

  const open = (host, trigger, panel, focusFirst) => {
    clearTimeout(closeTimer);
    if (current && current.panel === panel) return;
    close();
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    host.setAttribute('open', ''); // keeps Impact's hide-on-scroll from hiding an open header
    place(panel, trigger);
    current = { host, trigger, panel };
    if (focusFirst) {
      const first = panel.querySelector('input, a, button');
      if (first) first.focus({ preventScroll: true });
    }
  };

  all('[data-tw-item]', root).forEach((item) => {
    const trigger = item.querySelector('[data-tw-trigger]');
    const panel = item.querySelector('[data-tw-panel]');
    if (!trigger || !panel) return;

    item.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !finePointer.matches) return;
      clearTimeout(openTimer);
      openTimer = setTimeout(() => open(item, trigger, panel), current ? 0 : 70);
    });
    item.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
    });

    // Touch and keyboard: the first press opens the card, the second follows the link.
    trigger.addEventListener('click', (e) => {
      if (finePointer.matches && e.detail > 0) return;
      if (!current || current.panel !== panel) {
        e.preventDefault();
        open(item, trigger, panel, e.detail === 0);
      }
    });
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        open(item, trigger, panel, true);
      }
    });
  });

  root.addEventListener('pointerleave', (e) => {
    if (e.pointerType !== 'mouse' || pinned) return;
    clearTimeout(openTimer);
    closeTimer = setTimeout(close, 180);
  });
  root.addEventListener('pointerenter', () => clearTimeout(closeTimer));
  root.addEventListener('pointerover', (e) => {
    if (pinned && current && current.panel.contains(e.target)) pinned = false;
  });

  /* ----------------------------------------------------------- area list */

  const areaPanel = root.querySelector('[data-tw-area-panel]');
  const search = root.querySelector('[data-tw-area-search]');
  const empty = root.querySelector('[data-tw-area-empty]');

  const results = areaPanel && areaPanel.querySelector('[data-tw-results]');
  const areasHead = areaPanel && areaPanel.querySelector('[data-tw-areas-head]');
  let areaHits = 0;
  let storeHits = 0;
  let pending = false;

  const showEmpty = () => {
    if (empty) empty.hidden = areaHits > 0 || storeHits > 0 || pending;
  };

  const filter = () => {
    const q = (search.value || '').trim().toLowerCase();
    areaHits = 0;
    all('[data-tw-area]', areaPanel).forEach((el) => {
      const hit = !q || el.dataset.search.includes(q);
      el.parentElement.hidden = !hit;
      if (hit) areaHits += 1;
    });
    all('.tw-hdr__areas-all', areaPanel).forEach((li) => { li.hidden = Boolean(q); });
    // While searching, the matching areas get a heading of their own above the store results.
    if (areasHead) areasHead.hidden = !(results && q.length >= 2 && areaHits > 0);
    storeSearch(q);
    showEmpty();
  };

  /* -------------------------------------------------------- store search */

  // Shopify predictive search: a small JSON answer from Shopify's own servers. Nothing is fetched
  // until two letters are typed; a pause of 250 ms between keys sends one request, a newer one
  // cancels the older, and an answer is kept so typing the same word again costs nothing.
  const cache = new Map();
  let timer = 0;
  let controller = null;
  const supported = (() => {
    try { return JSON.parse(document.getElementById('shopify-features').textContent).predictiveSearch !== false; } catch (e) { return true; }
  })();

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, minimumFractionDigits: n % 1 ? 2 : 0 }).format(n); } catch (e) { return `$${n}`; }
  };
  const thumb = (url) => {
    if (!url) return '';
    try { const u = new URL(url, window.location.origin); u.searchParams.set('width', '96'); return u.toString(); } catch (e) { return url; }
  };
  // A workshop is an event product (its type says Event); a product with no type counts when its
  // title names a workshop or class, unless it is a gift card, a certificate or an upgrade.
  const isWorkshop = (p) => /event/i.test(p.type || '')
    || (!p.type && /workshop|class/i.test(p.title || '') && !/gift|certificate|upgrade/i.test(p.title || ''));
  // Products kept out of the box (add-ons, tests), by handle, from the section setting.
  const hidden = new Set(((results && results.dataset.hide) || '').split(',').map((h) => h.trim()).filter(Boolean));

  const clearResults = () => {
    if (!results) return;
    results.hidden = true;
    results.innerHTML = '';
    storeHits = 0;
  };

  const render = (data, q) => {
    const d = results.dataset;
    const products = (data.products || []).filter((p) => !hidden.has(p.handle));
    const pages = [...(data.collections || []), ...(data.pages || [])].slice(0, 4);
    const row = (p) => `<li><a class="tw-hdr__ritem" href="${esc(p.url)}">${p.image ? `<img src="${esc(thumb(p.image))}" alt="" width="48" height="48" loading="lazy">` : '<span class="tw-hdr__rimg"></span>'}<span class="tw-hdr__rtext"><span class="tw-hdr__rname">${esc(p.title)}</span><span class="tw-hdr__rmeta">${p.available === false ? esc(d.labelSoldout) : `${esc(d.labelFrom)} ${esc(money(p.price_min || p.price))}`}</span></span></a></li>`;
    const group = (label, items, make) => (items.length ? `<p class="tw-hdr__rlabel">${esc(label)}</p><ul class="tw-hdr__rlist" role="list">${items.map(make).join('')}</ul>` : '');
    const work = products.filter(isWorkshop);
    const shop = products.filter((p) => !isWorkshop(p));
    const page = (p) => `<li><a class="tw-hdr__ritem tw-hdr__ritem--page" href="${esc(p.url)}"><span class="tw-hdr__rname">${esc(p.title)}</span></a></li>`;
    storeHits = products.length + pages.length;
    const allUrl = `${d.searchUrl}?q=${encodeURIComponent(q)}`;
    // The group holding Shopify's best match comes first ("moon" leads with the Moon lamp kit).
    const workFirst = !shop.length || (work.length && products.indexOf(work[0]) < products.indexOf(shop[0]));
    const both = workFirst ? group(d.labelWorkshops, work, row) + group(d.labelShop, shop, row) : group(d.labelShop, shop, row) + group(d.labelWorkshops, work, row);
    results.innerHTML = both + group(d.labelPages, pages, page)
      + (storeHits ? `<a class="tw-hdr__rall" href="${esc(allUrl)}">${esc(d.labelAll.replace('[q]', q))}</a>` : '');
    results.hidden = storeHits === 0;
  };

  function storeSearch(raw) {
    if (!results || !supported) return;
    clearTimeout(timer);
    const q = raw.trim();
    if (q.length < 2) { if (controller) controller.abort(); pending = false; clearResults(); return; }
    const key = q.toLowerCase();
    if (cache.has(key)) { pending = false; render(cache.get(key), q); return; }
    pending = true;
    timer = setTimeout(async () => {
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,page', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', 'resources[options][fields]': 'title,product_type,variants.title,tag' });
      try {
        const response = await fetch(`${results.dataset.url}.json?${params}`, { signal: controller.signal });
        const data = (await response.json()).resources.results;
        cache.set(key, data);
        if ((search.value || '').trim().toLowerCase() !== key) return;
        pending = false;
        render(data, q);
      } catch (e) {
        if (e.name === 'AbortError') return;
        pending = false;
        clearResults();
      }
      showEmpty();
    }, 250);
  }

  const openArea = (anchor) => {
    if (!areaPanel) return;
    if (current && current.panel === areaPanel) { close(); return; }
    open(anchor, anchor, areaPanel, false);
    if (finePointer.matches && search) search.focus({ preventScroll: true });
  };

  root.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-tw-area-toggle]');
    if (toggle) { openArea(toggle); return; }

    const opener = e.target.closest('[data-tw-area-open]');
    if (opener) {
      const pill = all('[data-tw-area-toggle]', root).find((el) => el.offsetParent);
      if (!pill) return; // no box on this screen: the link goes to the search page
      e.preventDefault();
      if (!(current && current.panel === areaPanel)) openArea(pill);
      // The magnifier means typing: put the cursor in the box, on a phone too.
      if (opener.hasAttribute('data-tw-focus-search') && search) search.focus({ preventScroll: true });
      return;
    }

    if (e.target.closest('[data-tw-area-clear]')) {
      clearArea();
      close();
      if (search) { search.value = ''; filter(); }
      return;
    }

    const pick = e.target.closest('[data-tw-area-pick], [data-tw-area]');
    if (!pick) return;
    const key = pick.dataset.twAreaPick || pick.dataset.twArea;
    const option = choose(key, pick.dataset.twAreaPick ? 'card' : 'list');
    if (!option || pick.dataset.twAreaPick) return;

    const after = root.dataset.afterPick;
    if (after === 'navigate' && option.dataset.url) { window.location.href = option.dataset.url; return; }
    close();
    if (search) { search.value = ''; filter(); }
    if (after === 'workshops' && finePointer.matches) {
      const work = root.querySelector('[data-tw-item] [data-tw-area-heading]');
      const item = work && work.closest('[data-tw-item]');
      if (item && item.offsetParent) {
        open(item, item.querySelector('[data-tw-trigger]'), item.querySelector('[data-tw-panel]'));
        pinned = true;
      }
    }
  });

  // Phone menu: picking a city chip inside the drawer scopes it the same way.
  document.addEventListener('click', (e) => {
    const pick = e.target.closest('.tw-hdr-drawer [data-tw-area-pick]');
    if (pick) choose(pick.dataset.twAreaPick, 'drawer');
    const opener = e.target.closest('.tw-hdr-drawer [data-tw-area-open]');
    if (opener) {
      const drawer = opener.closest('x-drawer');
      if (drawer && drawer.hide) drawer.hide();
      const pill = all('[data-tw-area-toggle]', root).find((el) => el.offsetParent);
      if (pill) setTimeout(() => openArea(pill), 250);
    }
  });

  if (search) {
    // Everything that can be reached from the box, in screen order, for the arrow keys.
    const stops = () => [
      ...all('[data-tw-area]', areaPanel).filter((el) => !el.parentElement.hidden),
      ...(results && !results.hidden ? all('a', results) : []),
    ];
    search.addEventListener('input', filter);
    search.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        const first = stops()[0];
        if (first) { e.preventDefault(); first.focus(); }
        return;
      }
      if (e.key !== 'Enter') return;
      // A city takes you to its area; anything else goes to the search page (the form's own submit).
      const first = all('[data-tw-area]', areaPanel).find((el) => !el.parentElement.hidden);
      const q = (search.value || '').trim();
      if (first && q) { e.preventDefault(); first.click(); return; }
      if (!q) e.preventDefault();
    });
    areaPanel.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const list = stops();
      const i = list.indexOf(document.activeElement);
      if (i < 0) return;
      e.preventDefault();
      const next = list[i + (e.key === 'ArrowDown' ? 1 : -1)];
      (next || search).focus();
    });
  }

  /* -------------------------------------------------------------- closing */

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !current) return;
    const back = current.trigger;
    close();
    back.focus({ preventScroll: true });
  });
  document.addEventListener('pointerdown', (e) => {
    if (current && !root.contains(e.target)) close();
  });
  root.addEventListener('focusout', (e) => {
    if (current && e.relatedTarget && !root.contains(e.relatedTarget)) close();
  });
  // Only a width change closes: phones fire resize when the address bar slides on scroll.
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    close();
  }, { passive: true });

  // Phone menu: one section open at a time.
  all('[data-tw-acc]').forEach((d) => d.addEventListener('toggle', () => {
    if (d.open) all('[data-tw-acc]').forEach((o) => { if (o !== d) o.open = false; });
  }));

  // Editor: selecting a block opens its card so it can be edited in place.
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:block:select', (e) => {
      const item = e.target.closest && e.target.closest('[data-tw-item]');
      if (item && root.contains(item)) open(item, item.querySelector('[data-tw-trigger]'), item.querySelector('[data-tw-panel]'));
    });
    document.addEventListener('shopify:block:deselect', close);
  }
})();
